import { useParams, Link } from 'react-router-dom';
import { useSEO } from '@/hooks/useSEO';
import { videos } from '@/mocks/videos';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const video = videos.find((v) => v.id === Number(id));

  if (!video) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-20 text-center">
        <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100">
          <i className="ri-emotion-sad-line text-4xl text-foreground-400"></i>
        </div>
        <h1 className="mt-6 text-xl font-bold text-foreground-900">内容未找到</h1>
        <p className="mt-2 text-foreground-500">您访问的内容不存在或已下架</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors"
        >
          <i className="ri-home-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  useSEO({
    title: `${video.title} - 神马视频免费在线观看 | ${video.category}`,
    description: `${video.title} - ${video.description} 神马视频${video.category}为您提供${video.title}高清免费在线观看，${video.quality}画质不缓冲。`,
    keywords: `${video.title},${video.category},神马视频,免费在线观看,高清不缓冲,${video.tags.join(',')}`,
    ogType: 'video.movie',
    ogImage: video.coverImage,
  });

  const relatedVideos = videos.filter(
    (v) => v.id !== video.id && (v.categorySlug === video.categorySlug || v.tags.some((t) => video.tags.includes(t)))
  ).slice(0, 4);

  const breadcrumbs = [
    { name: '首页', href: '/' },
    { name: video.category, href: `/category/${video.categorySlug}` },
    { name: video.title, href: `/article/${video.id}` },
  ];

  return (
    <>
      <div className="bg-background-100 border-b border-background-200">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-3">
          <nav aria-label="面包屑导航" className="flex items-center flex-wrap gap-2 text-xs text-foreground-500">
            {breadcrumbs.map((crumb, i) => (
              <span key={crumb.href} className="flex items-center gap-2">
                {i > 0 && <i className="ri-arrow-right-s-line"></i>}
                {i < breadcrumbs.length - 1 ? (
                  <Link to={crumb.href} className="hover:text-primary-600 transition-colors">{crumb.name}</Link>
                ) : (
                  <span className="text-foreground-700 font-medium">{crumb.name}</span>
                )}
              </span>
            ))}
          </nav>
        </div>
      </div>

      <article className="max-w-[1400px] mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8 md:gap-10">
          <div>
            <div className="relative aspect-video rounded-lg overflow-hidden">
              <img
                src={video.coverImage}
                alt={video.title}
                className="w-full h-full object-cover"
                title={`${video.title} - ${video.category}高清在线观看`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-950/60 via-transparent to-transparent flex items-center justify-center">
                <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer">
                  <i className="ri-play-fill text-3xl md:text-4xl"></i>
                </div>
              </div>
            </div>

            <h1 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mt-6">
              {video.title}
            </h1>

            <div className="flex flex-wrap items-center gap-3 md:gap-4 mt-3 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-500"></i>
                <strong className="text-foreground-900 font-semibold">{video.rating}</strong> 分
              </span>
              <span className="text-foreground-300">|</span>
              <span>{video.year}</span>
              <span className="text-foreground-300">|</span>
              <span>{video.duration}</span>
              <span className="text-foreground-300">|</span>
              <span>{video.quality}</span>
              <span className="text-foreground-300">|</span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {video.views > 10000 ? `${(video.views / 10000).toFixed(0)}万次` : `${video.views}次`}播放
              </span>
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
              {video.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t border-background-200">
              <h2 className="text-lg font-semibold text-foreground-900 font-heading mb-3">影片简介</h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                {video.description}
              </p>
            </div>

            <div className="mt-6 pt-6 border-t border-background-200">
              <h2 className="text-lg font-semibold text-foreground-900 font-heading mb-3">影片信息</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-foreground-500">上映年份：</span>
                  <span className="text-foreground-700">{video.year}</span>
                </div>
                <div>
                  <span className="text-foreground-500">视频品牌：</span>
                  <Link to={`/category/${video.categorySlug}`} className="text-primary-600 hover:text-primary-700">
                    {video.category}
                  </Link>
                </div>
                <div>
                  <span className="text-foreground-500">画质：</span>
                  <span className="text-foreground-700">{video.quality}</span>
                </div>
                <div>
                  <span className="text-foreground-500">时长：</span>
                  <span className="text-foreground-700">{video.duration}</span>
                </div>
                <div>
                  <span className="text-foreground-500">评分：</span>
                  <span className="text-foreground-700">{video.rating} 分</span>
                </div>
                <div>
                  <span className="text-foreground-500">上线日期：</span>
                  <span className="text-foreground-700">{video.publishDate}</span>
                </div>
              </div>
            </div>
          </div>

          <aside>
            <div className="bg-background-100 rounded-lg p-5 sticky top-20">
              <h3 className="text-base font-semibold text-foreground-900 font-heading mb-4">相关推荐</h3>
              <div className="space-y-4">
                {relatedVideos.map((rv) => (
                  <Link
                    key={rv.id}
                    to={`/article/${rv.id}`}
                    className="flex gap-3 group"
                  >
                    <div className="w-28 h-16 rounded overflow-hidden flex-shrink-0">
                      <img
                        src={rv.coverImage}
                        alt={rv.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        title={rv.title}
                      />
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                        {rv.title}
                      </h4>
                      <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-500">
                        <span className="flex items-center gap-0.5">
                          <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                          {rv.rating}
                        </span>
                        <span>{rv.year}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </article>

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": breadcrumbs.map((crumb, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "name": crumb.name,
            "item": `https://www.shenmashipin.com${crumb.href}`
          }))
        })
      }} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": video.title,
          "description": video.description,
          "datePublished": video.publishDate,
          "url": `https://www.shenmashipin.com/article/${video.id}`,
          "image": video.coverImage,
          "author": {
            "@type": "Organization",
            "name": video.category
          },
          "publisher": {
            "@type": "Organization",
            "name": "神马视频",
            "url": "https://www.shenmashipin.com/"
          }
        })
      }} />
    </>
  );
}